import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { COMPANY, NAV_LINKS } from '../../data/siteData';
import Button from '../common/Button';

// ---- Navbar ----
// Smart nav links — if on homepage: smooth scroll to anchor
// If on another page (e.g. /books): navigate to /#section first

export default function Navbar() {
  const [scrolled,         setScrolled]         = useState(false);
  const [menuOpen,         setMenuOpen]          = useState(false);
  const [booksOpen,        setBooksOpen]         = useState(false);
  const [mobileBooksOpen,  setMobileBooksOpen]   = useState(false);
  const dropdownRef = useRef(null);
  const location    = useLocation();
  const navigate    = useNavigate();
  const isHome      = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setBooksOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Smart link handler — scrolls on home, navigates + scrolls from other pages
  const handleNavClick = (e, href) => {
    e.preventDefault();
    closeAll();

    if (href.startsWith('#')) {
      if (isHome) {
        // Already on homepage — just scroll
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        // On another page — go to homepage then scroll after load
        navigate('/');
        setTimeout(() => {
          const el = document.querySelector(href);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 400);
      }
    } else {
      navigate(href);
    }
  };

  const closeAll = () => {
    setMenuOpen(false);
    setBooksOpen(false);
    setMobileBooksOpen(false);
  };

  const linkStyle = {
    textDecoration: 'none', color: 'var(--text-secondary)',
    fontSize: '13px', fontWeight: 500,
    letterSpacing: '0.07em', textTransform: 'uppercase',
    transition: 'color 0.2s', cursor: 'pointer',
    background: 'none', border: 'none',
    fontFamily: 'var(--font-sans)', padding: 0,
  };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 48px',
        background: scrolled || menuOpen ? 'rgba(13,15,20,0.98)' : 'transparent',
        borderBottom: scrolled ? '1px solid rgba(201,168,76,0.12)' : '1px solid transparent',
        backdropFilter: scrolled || menuOpen ? 'blur(12px)' : 'none',
        transition: 'all 0.3s ease',
      }}>

        {/* Logo */}
        <button onClick={(e) => handleNavClick(e, '/')} style={{
          ...linkStyle, textTransform: 'none', zIndex: 10,
        }}>
          <span style={{ fontFamily: 'var(--font-serif)', fontSize: '18px', fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.02em' }}>
            {COMPANY.name}
          </span>
        </button>

        {/* Desktop Links */}
        <ul style={{ display: 'flex', gap: '28px', listStyle: 'none', alignItems: 'center' }} className="desktop-nav">
          {NAV_LINKS.map(link => (
            <li key={link.label}>
              <button
                onClick={(e) => handleNavClick(e, link.href)}
                style={linkStyle}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
              >
                {link.label}
              </button>
            </li>
          ))}

          {/* Books dropdown */}
          <li ref={dropdownRef} style={{ position: 'relative' }}>
            <button onClick={() => setBooksOpen(!booksOpen)} style={{
              ...linkStyle,
              color: booksOpen ? 'var(--gold)' : 'var(--text-secondary)',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}>
              Books
              <svg width="10" height="7" viewBox="0 0 10 7" fill="none"
                style={{ transform: booksOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>

            {booksOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 14px)', left: '50%',
                transform: 'translateX(-50%)',
                background: '#141720',
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: '10px', overflow: 'hidden',
                minWidth: '210px',
                boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
                zIndex: 200,
              }}>
                {[
                  { href: '/books',        icon: '📚', label: 'Other Authors', sub: 'Free PDFs — Preview & Download' },
                  { href: '/books?tab=pmg', icon: '🔒', label: 'PMG Books',    sub: 'Exclusive — Coming Soon', gold: true },
                ].map((item, i) => (
                  <button key={item.label}
                    onClick={(e) => handleNavClick(e, item.href)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '14px 18px', width: '100%',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      borderBottom: i === 0 ? '1px solid rgba(201,168,76,0.08)' : 'none',
                      transition: 'background 0.15s', textAlign: 'left',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '18px' }}>{item.icon}</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{item.label}</div>
                      <div style={{ fontSize: '11px', color: item.gold ? 'var(--gold)' : 'var(--text-secondary)', marginTop: '1px' }}>{item.sub}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </li>
        </ul>

        {/* Desktop CTA */}
        <div className="desktop-cta">
          <Button variant="primary" size="sm" onClick={(e) => handleNavClick(e, '#contact')}>Get Started</Button>
        </div>

        {/* Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="hamburger"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'none', flexDirection: 'column',
            gap: '5px', padding: '4px', zIndex: 10,
          }}
          aria-label="Toggle menu"
        >
          {[0, 1, 2].map(i => (
            <span key={i} style={{
              display: 'block', width: '24px', height: '2px',
              background: 'var(--gold)', borderRadius: '2px',
              transition: 'all 0.3s ease',
              transform: menuOpen
                ? i === 0 ? 'rotate(45deg) translate(5px, 5px)'
                  : i === 2 ? 'rotate(-45deg) translate(5px, -5px)'
                  : 'none'
                : 'none',
              opacity: menuOpen && i === 1 ? 0 : 1,
            }} />
          ))}
        </button>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: 'rgba(13,15,20,0.99)',
          display: 'flex', flexDirection: 'column',
          paddingTop: '80px', paddingBottom: '40px',
          overflowY: 'auto',
        }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 24px', gap: '4px' }}>

            {NAV_LINKS.map(link => (
              <button key={link.label}
                onClick={(e) => handleNavClick(e, link.href)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  textAlign: 'left', color: 'var(--text-primary)',
                  fontSize: '22px', fontFamily: 'var(--font-serif)',
                  fontWeight: 600, padding: '14px 0',
                  borderBottom: '1px solid rgba(201,168,76,0.08)',
                  width: '100%', transition: 'color 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-primary)'}
              >
                {link.label}
              </button>
            ))}

            {/* Mobile Books accordion */}
            <div>
              <button onClick={() => setMobileBooksOpen(!mobileBooksOpen)} style={{
                width: '100%', background: 'none', border: 'none',
                borderBottom: '1px solid rgba(201,168,76,0.08)',
                color: mobileBooksOpen ? 'var(--gold)' : 'var(--text-primary)',
                fontSize: '22px', fontFamily: 'var(--font-serif)',
                fontWeight: 600, padding: '14px 0',
                cursor: 'pointer', textAlign: 'left',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                Books
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none"
                  style={{ transform: mobileBooksOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                  <path d="M1 1l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>

              {mobileBooksOpen && (
                <div style={{ padding: '10px 0 10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { href: '/books',        icon: '📚', label: 'Other Authors', sub: 'Free PDFs — Preview & Download' },
                    { href: '/books?tab=pmg', icon: '🔒', label: 'PMG Books',    sub: 'Exclusive — Coming Soon' },
                  ].map(item => (
                    <button key={item.label}
                      onClick={(e) => handleNavClick(e, item.href)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '12px 16px', width: '100%',
                        background: 'rgba(201,168,76,0.04)',
                        border: '1px solid rgba(201,168,76,0.1)',
                        borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)' }}>{item.label}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', fontFamily: 'var(--font-sans)' }}>{item.sub}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile CTA */}
          <div style={{ padding: '24px 24px 0' }}>
            <button
              onClick={(e) => handleNavClick(e, '#contact')}
              style={{
                display: 'block', width: '100%', textAlign: 'center',
                background: 'var(--gold)', color: 'var(--ink)',
                padding: '16px', borderRadius: '6px',
                fontFamily: 'var(--font-sans)', fontSize: '15px',
                fontWeight: 500, border: 'none', cursor: 'pointer',
                letterSpacing: '0.04em',
              }}
            >
              Get Started
            </button>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .desktop-cta { display: none !important; }
          .hamburger   { display: flex !important; }
          nav { padding: 0 20px !important; }
        }
      `}</style>
    </>
  );
}
